import { badmadRobotQuests } from "@/data-access/quests/badmadRobotQuests";
import { battleOfSoulsQuests } from "@/data-access/quests/battleOfSoulsQuests";
import { battleRiseQuests } from "@/data-access/quests/battleRiseQuests";
import { bitpoolQuests } from "@/data-access/quests/bitpoolQuests";
import { champzQuests } from "@/data-access/quests/champzQuests";
import { chessUniverseQuests } from "@/data-access/quests/chessUniverseQuests";
import { chumbiValleyQuests } from "@/data-access/quests/chumbiValleyQuests";
import { darkMachineQuests } from "@/data-access/quests/darkMachineQuests";
import { echoesOfCryptaliaQuests } from "@/data-access/quests/echoesOfCryptaliaQuests";
import { galaxyOfLemuriaQuests } from "@/data-access/quests/galaxyOfLemuriaQuest";
import { godsUnchainedQuests } from "@/data-access/quests/godsUnchainedQuests";
import { immortalRisingQuests } from "@/data-access/quests/immortalRisingQuests";
import { inMatch3Quests } from "@/data-access/quests/inMatch3Quests";
import { kingArthurQuests } from "@/data-access/quests/kingArthurQuests";
import { marbleXQuestsList } from "@/data-access/quests/marbleXQuests";
import { normaInMetalandQuests } from "@/data-access/quests/normaInMetalandQuests";
import { normaInMetalandQuestsPoints } from "@/data-access/quests/normaInMetalandQuestsPoints";
import { OverknightsPointsQuests } from "@/data-access/quests/overknightsPointsQuests";
import { overknightsQuests } from "@/data-access/quests/overknightsQuests";
import { overtakeQuestsList } from "@/data-access/quests/overtakeQuestsList";
import {
  ravenQuestQuestsMain,
  ravenQuestQuestsVariantA,
} from "@/data-access/quests/ravenQuestQuests";
import { selfMadeBillionaireQuests } from "@/data-access/quests/selfMadeBillionaireQuests";
import { somnisQuests } from "@/data-access/quests/somnisQuests";
import { swordsOfBloodQuests } from "@/data-access/quests/swordsOfBloodQuests";
import { tokyoBeastQuests } from "@/data-access/quests/tokyoBeastQuests";
import {
  treeVerseQuests,
  treeVerseQuestsVariantB,
} from "@/data-access/quests/treeverseQuests";
import { treeverseQuestsPoints } from "@/data-access/quests/treeverseQuestsPoints";
import { triumphPointsQuests } from "@/data-access/quests/triumphPointsQuests";
import { zipClashQuests } from "@/data-access/quests/zipClashQuests";
import type { QuestGroup } from "@ui-kit/QuestList/types";
import { useMemo } from "react";

const GAMES_WITH_ONBOARDING_QUESTS = [
  "ravenquest",
  "chessuniverse",
  "overtake",
  "badmad-robots",
  "king-arthur-legends-rise",
  "norma-in-metaland",
  "dark-machine",
  "immortal-rising-2",
  "swords-of-blood",
  "marblex",
  "champignons-of-arborethia",
  "champignons-of-arborethia-b",
  "treeverse",
  "echoes-of-cryptalia",
  "gods-unchained",
  "zipclash",
  "somnis-rumble-rush",
  "bitpool",
  "tokyo-beast",
  "battle-of-souls",
  "self-made-billionaire",
  "the-galaxy-of-lemuria",
  "inmatch3",
  "overknights",
  "battlerise",
  "chumbi-valley",
  // add game uids here
];

const useGetGameQuests = (uid: string, isPoints: boolean) => {
  const ravenQuestQuests: QuestGroup[] = useMemo(
    () => [...ravenQuestQuestsMain, ...ravenQuestQuestsVariantA],
    [],
  );

  switch (uid) {
    case "ravenquest":
      return ravenQuestQuests;
    case "chessuniverse":
      return chessUniverseQuests;
    case "overtake":
      return overtakeQuestsList;
    case "badmad-robots":
      return badmadRobotQuests;
    case "king-arthur-legends-rise":
      return kingArthurQuests;
    case "norma-in-metaland":
      return normaInMetalandQuests;
    case "dark-machine":
      return darkMachineQuests;
    case "immortal-rising-2":
      return immortalRisingQuests;
    case "swords-of-blood":
      return swordsOfBloodQuests;
    case "marblex":
      return marbleXQuestsList;
    case "champignons-of-arborethia":
    case "champignons-of-arborethia-b":
      return champzQuests;
    case "treeverse":
      if (isPoints) {
        return treeverseQuestsPoints;
      }
      return treeVerseQuests;
    case "echoes-of-cryptalia":
      return echoesOfCryptaliaQuests;
    case "gods-unchained":
      return godsUnchainedQuests;
    case "zipclash":
      return zipClashQuests;
    case "somnis-rumble-rush":
      return somnisQuests;
    case "bitpool":
      return bitpoolQuests;
    case "tokyo-beast":
      return tokyoBeastQuests;
    case "battle-of-souls":
      return battleOfSoulsQuests;
    case "self-made-billionaire":
      return selfMadeBillionaireQuests;
    case "the-galaxy-of-lemuria":
      return galaxyOfLemuriaQuests;
    case "inmatch3":
      return inMatch3Quests;
    case "overknights":
      return overknightsQuests;
    case "battlerise":
      return battleRiseQuests;
    case "chumbi-valley":
      return chumbiValleyQuests;
    // TODO: add quests for other games
    default:
      return [];
  }
};

export const useOnboardingQuestData = (
  uid: string,
  isPointsTemplate = false,
) => {
  const gameQuests = useGetGameQuests(uid, isPointsTemplate);
  const payload = useMemo(
    () => ({
      questsGroups: gameQuests,
      hasOnboardingQuests: GAMES_WITH_ONBOARDING_QUESTS.includes(uid),
    }),
    [uid, gameQuests],
  );
  return payload;
};

export const getGamesQuests = (uid: string) => {
  let quests: QuestGroup[] = [];
  switch (uid) {
    case "ravenquest":
      quests = [...ravenQuestQuestsMain, ...ravenQuestQuestsVariantA];
      break;
    case "chessuniverse":
      quests = chessUniverseQuests;
      break;
    case "overtake":
      quests = overtakeQuestsList;
      break;
    case "badmad-robots":
      quests = badmadRobotQuests;
      break;
    case "king-arthur-legends-rise":
      quests = kingArthurQuests;
      break;
    case "norma-in-metaland":
      quests = normaInMetalandQuestsPoints;
      break;
    case "dark-machine":
      quests = darkMachineQuests;
      break;
    case "immortal-rising-2":
      quests = immortalRisingQuests;
      break;
    case "swords-of-blood":
      quests = swordsOfBloodQuests;
      break;
    case "marblex":
      quests = marbleXQuestsList;
      break;
    case "champignons-of-arborethia":
    case "champignons-of-arborethia-b":
      quests = champzQuests;
      break;
    case "treeverse":
      quests = treeVerseQuestsVariantB;
      break;
    case "echoes-of-cryptalia":
      quests = echoesOfCryptaliaQuests;
      break;
    case "gods-unchained":
      quests = godsUnchainedQuests;
      break;
    case "zipclash":
      quests = zipClashQuests;
      break;
    case "somnis-rumble-rush":
      quests = somnisQuests;
      break;
    case "bitpool":
      quests = bitpoolQuests;
      break;
    case "tokyo-beast":
      quests = tokyoBeastQuests;
      break;
    case "battle-of-souls":
      quests = battleOfSoulsQuests;
      break;
    case "self-made-billionaire":
      quests = selfMadeBillionaireQuests;
      break;
    case "the-galaxy-of-lemuria":
      quests = galaxyOfLemuriaQuests;
      break;
    case "inmatch3":
      quests = inMatch3Quests;
      break;
    case "overknights":
      quests = OverknightsPointsQuests;
      break;
    case "battlerise":
      quests = battleRiseQuests;
      break;
    case "chumbi-valley":
      quests = chumbiValleyQuests;
      break;
    case "triumph":
      quests = triumphPointsQuests;
      break;
    // TODO: add quests for other games
    default:
      quests = [];
  }
  return {
    questsGroups: quests,
    hasOnboardingQuests: GAMES_WITH_ONBOARDING_QUESTS.includes(uid),
  };
};
